import React, { useState, useRef, useEffect } from "react";
import {Footer} from "../../shared";
import Navbar from "../homepage/Navbar";
import Daas from "./Daas";
import GIF from "../../assets/image/png/Drone.gif";
import {
  IoArrowForwardCircleOutline,
  IoArrowForwardOutline,
} from "react-icons/io5";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { toast } from "react-toastify";
import Autocomplete from "react-google-autocomplete";
import ZohoLeadForm from "./ZohoLeadForm";
import { fireGtagEvent } from "../../utils/gtagEvents";
import { Header } from "../../shared";

const categories = {
  General: [
    {
      question: "What is Drone-as-a-Service (DaaS)?",
      answer:
        "DaaS is an on-demand model that allows individuals, businesses, and government agencies to hire certified drone operators and fleets for specific projects — without the need to own or maintain drone equipment.",
    },
  ],
  "For Service Seekers": [
    {
      question: "Are all drone providers certified?",
      answer:
        "Yes. All service providers on Drone Planet are DGCA-certified and operate registered drones through the Digital Sky Platform, ensuring legal, safe, and compliant operations.",
    },
    {
      question: "How is pricing determined for DaaS?",
      answer:
        "Pricing is based on:\n• Drone type and payload\n• Project location and duration\n• Complexity and regulatory needs\n\nTypical pricing ranges from ₹2,500 to ₹15,000 per hour/day. Transparent quotes are provided upfront — no hidden fees.",
    },
    {
      question: "How do I hire a drone service?",
      answer:
        "1. Post your project requirements\n2. Receive and compare bids from verified providers\n3. Select a provider and launch the project in under 48 hours",
    },
    {
      question: "How soon can a project be launched after booking?",
      answer:
        "Once bids are received and a provider is selected, most projects are launched within 48 hours.",
    },
    {
      question: "Is there a minimum booking requirement?",
      answer:
        "Most providers accept hourly or day-based bookings. Minimums vary by provider and project type, but Drone Planet supports flexible, short-term engagements.",
    },
  ],
  "For Service Providers": [
    {
      question: "Who can register as a service provider on Drone Planet?",
      answer:
        "Any drone operator or fleet owner with a valid DGCA Remote Pilot License (RPL) or SUAS certificate, a Digital Sky-registered drone, and a GSTIN for invoicing can register and start offering services.",
    },
    {
      question: "How do I earn money through the platform?",
      answer:
        "You earn on a per-project basis. Once you complete a job, funds are released via escrow-secured payments, ensuring timely and protected payouts.",
    },
    {
      question: "What are the compliance requirements to operate?",
      answer:
        "You must:\n• Hold a valid DGCA RPL or SUAS certificate\n• Operate a registered drone on Digital Sky\n• Follow NPNT protocols and DGCA airspace rules\n• Maintain flight logs and documentation as required",
    },
    {
      question: "Is Drone Planet only for large fleets?",
      answer:
        "No. Both individual certified pilots and large fleet operators can join. The platform supports one-off jobs as well as recurring enterprise contracts.",
    },
    {
      question: "Do I need to handle flight permissions and insurance?",
      answer:
        "Yes, you are responsible for flight permissions via the Digital Sky Platform. Drone Planet includes third-party liability insurance with each project to protect you and the client.",
    },
    {
      question: "How do I get started?",
      answer:
        "1. Register as a provider on Drone Planet\n2. Upload your credentials (DGCA license, drone registration, GSTIN)\n3. Start receiving project bids and earning from your drone skills",
    },
  ],
};
const FaqItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border rounded-lg">
      <button
        className={`w-full text-left px-4 py-3 flex justify-between items-start ${
          isOpen ? "bg-green-100" : "bg-white hover:bg-gray-100"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-gray-800">{question}</span>
        <span className="mt-1">
          {isOpen ? (
            <FaChevronUp className="text-green-600" size={14} />
          ) : (
            <FaChevronDown className="text-gray-500" size={14} />
          )}
        </span>
      </button>
      {isOpen && (
        <div className="px-4 py-2 text-sm text-gray-700 bg-green-50 border-t">
          {answer.split("\n").map((line, idx) => (
            <p key={idx} className="mb-1 last:mb-0">
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ UTM helper (place ABOVE component)
const getUTM = (param) => {
  return new URLSearchParams(window.location.search).get(param) || "";
};

const DaasLandingpage = () => {
  const [activeTab, setActiveTab] = useState("enterprises");
  const formSectionRef = useRef(null);
  const [showForm, setShowForm] = useState(false);
  const [userType, setUserType] = useState("Client");
  const [zohoPayload, setZohoPayload] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "", // <-- added phone
    address: "",
    crop: "",
  });

  const validateForm = () => {
    if (
      !formData.name ||
      !formData.email ||
      !formData.address ||
      !formData.crop ||
      !formData.phone
    ) {
      toast.error("All fields are required!");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Enter a valid email address!");
      return false;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      toast.error("Phone number must be 10 digits and numeric!");
      return false;
    }

    return true;
  };

  const handlePlaceSelected = (place) => {
    if (!place?.formatted_address) return;

    setFormData({
      ...formData,
      address: place.formatted_address,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    fireGtagEvent("qualify_lead");
    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        userType, // Client or Operator
      };

      const res = await fetch(
        process.env.REACT_APP_API_URL + "/request-service",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();

      if (res.ok) {
        toast.success(data.message || "Form submitted successfully!");

        // 🔹 Trigger Zoho Web-to-Lead
        setZohoPayload({
          ...payload,
          utmSource: getUTM("utm_source"),
          utmMedium: getUTM("utm_medium"),
          utmCampaign: getUTM("utm_campaign"),
          utmContent: getUTM("utm_content"),
        });

        // reset UI form
        setFormData({
          name: "",
          email: "",
          address: "",
          crop: "",
          phone: "",
        });
        setUserType("Client");

        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(data.message || "Failed to submit form. Try again!");
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error("Something went wrong!");
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const daasRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState("General");

  return (
    <div className="relative min-h-screen bg-green-50 font-sans">
      <Header />

      {/* Hero Section */}
      {/* HERO SECTION WITH FORM */}
      <div className="relative min-h-[calc(100vh-72px)] pt-[72px] pb-12 flex items-center overflow-hidden">
        {/* Background Image */}
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20drone%20footage%20with%20stunning%20landscape%20view%2C%20professional%20high-quality%20cinematic%20drone%20shot%20over%20mountains%20and%20forests%2C%20dramatic%20lighting%2C%20high%20resolution%2C%20photorealistic%2C%20breathtaking%20aerial%20perspective%2C%20golden%20hour%20lighting&width=1440&height=600&seq=1&orientation=landscape"
          alt="Drone aerial view"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60" />

        {/* CONTENT */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* LEFT CONTENT */}
            <div className="text-white">
              <span className="inline-block bg-green-600/90 text-white text-xs font-medium px-4 py-1.5 rounded-full mb-6">
                Welcome to Drone Market
              </span>

              <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
                Connect Operators & Seekers.
                <br />
                <span className="text-green-400">Get the Job Done.</span>
              </h1>

              <p className="max-w-xl text-gray-200 text-sm md:text-base mb-8">
                Are you a drone operator looking for work, or a client in need
                of drone services? We connect skilled operators with verified
                clients for aerial photography, mapping, inspections and more.
              </p>
            </div>

            {/* RIGHT FORM */}
            <div className="bg-white rounded-xl shadow-2xl p-6 border max-h-[85vh] overflow-y-auto w-full max-w-md ml-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Register Now
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Share your requirement and we’ll connect you.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                {/* USER TYPE */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    I am a
                  </p>
                  <div className="flex gap-3">
                    {["Client", "Operator"].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setUserType(type)}
                        className={`flex-1 py-2 rounded-lg border text-sm font-medium transition
                    ${
                      userType === type
                        ? "bg-green-600 text-white border-green-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
                    }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <input
                  type="text"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-600 outline-none"
                  required
                />

                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-600 outline-none"
                  required
                />

                <input
                  type="text"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  maxLength={10}
                  className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-600 outline-none"
                />

                <Autocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_KEY}
                  onPlaceSelected={(place) => {
                    setFormData((prev) => ({
                      ...prev,
                      address: place.formatted_address || place.name || "",
                    }));
                  }}
                  options={{
                    types: ["(cities)"],
                    componentRestrictions: { country: "in" },
                  }}
                  placeholder="City"
                  className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-600 outline-none"
                />

                <input
                  type="text"
                  placeholder="Service Name"
                  value={formData.crop}
                  onChange={(e) =>
                    setFormData({ ...formData, crop: e.target.value })
                  }
                  className="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-600 outline-none"
                />

                <button
                  type="submit"
                  className="w-full bg-green-700 hover:bg-green-800 text-white font-medium py-2.5 rounded-lg transition"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <section className="bg-gray-100 py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-semibold text-gray-800">
              Our Reach & Services
            </h2>
            <p className="text-gray-600 mt-2 text-base sm:text-lg">
              Delivering drone-powered solutions across industries and
              geographies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10">
            {/* Services Offered */}
            <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-green-800 mb-3 sm:mb-4">
                Services We Offer
              </h3>
              <ul className="space-y-3 sm:space-y-4 text-gray-700 text-base sm:text-lg">
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="text-green-700 text-lg sm:text-xl">🚜</span>
                  Agricultural Survey
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="text-green-700 text-lg sm:text-xl">🏗️</span>
                  Infrastructure Inspection
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="text-green-700 text-lg sm:text-xl">🎥</span>
                  Event Coverage
                </li>
                <li className="flex items-start gap-2 sm:gap-3">
                  <span className="text-green-700 text-lg sm:text-xl">🚨</span>
                  Emergency Response
                </li>
              </ul>
            </div>

            {/* States Covered */}
            <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-semibold text-green-800 mb-3 sm:mb-4">
                States We Serve
              </h3>
              <ul className="grid grid-cols-2 gap-y-3 sm:gap-y-4 text-gray-700 text-base sm:text-lg">
                <li>📍 Maharashtra</li>
                <li>📍 Karnataka</li>
                <li>📍 Tamil Nadu</li>
                <li>📍 Uttar Pradesh</li>
                <li>📍 Gujarat</li>
                <li>📍 Punjab</li>
                <li>📍 Rajasthan</li>
                <li>📍 And More...</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Cards */}
      <div className="container mx-auto px-6 py-20">
        {/* Heading */}
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900">
            Why Choose Drone Market?
          </h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
            Whether you’re an operator or seeking services, we provide the tools
            and connections you need to succeed.
          </p>
        </div>

        {/* Two Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* For Operators */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">For Operators</h3>
            <p className="text-sm text-gray-500 mb-6">
              Grow your drone business with steady work opportunities
            </p>

            <div className="space-y-4">
              {[
                {
                  title: "Find projects",
                  desc: "Browse available drone projects in your area and apply to those that match your skills.",
                },
                {
                  title: "Set your own rates",
                  desc: "You control your pricing and negotiate directly with clients for fair compensation.",
                },
                {
                  title: "Flexible scheduling",
                  desc: "Work on your own schedule and choose projects that fit your availability.",
                },
                {
                  title: "Secure payments",
                  desc: "Get paid safely and on time through our secure payment processing system.",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 items-start p-4 border rounded-lg bg-white"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-green-50 text-green-600 text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-500 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* For Seekers */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">For Seekers</h3>
            <p className="text-sm text-gray-500 mb-6">
              Find the perfect drone operator for your project needs
            </p>

            <div className="space-y-4">
              {[
                {
                  title: "Access to experts",
                  desc: "Connect with verified and experienced drone operators for your specific needs.",
                },
                {
                  title: "Fast turnaround",
                  desc: "Get your project completed quickly with our network of available operators.",
                },
                {
                  title: "Competitive pricing",
                  desc: "Compare quotes from multiple operators to find the best value for your budget.",
                },
                {
                  title: "Quality assurance",
                  desc: "Review operator ratings and portfolios to ensure high-quality deliverables.",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 items-start p-4 border rounded-lg bg-white"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-green-50 text-green-600 text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{item.title}</h4>
                    <p className="text-sm text-gray-500 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 border-t pt-10">
          {[
            { value: "500+", label: "Active Operators" },
            { value: "5,000+", label: "Projects Completed" },
            { value: "2 Hours", label: "Average Response" },
            { value: "98%", label: "Satisfaction Rate" },
          ].map((stat, idx) => (
            <div key={idx} className="text-center">
              <h3 className="text-2xl font-bold text-green-600">
                {stat.value}
              </h3>
              <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="bg-green-100 py-20">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-14">
            <h2 className="text-3xl font-semibold text-gray-900">
              What You Get
            </h2>
            <p className="text-gray-600 mt-3">
              Comprehensive drone services for every industry and application
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Aerial Photography",
                desc: "Professional aerial photos for real estate, events, and marketing campaigns.",
                icon: "📷",
              },
              {
                title: "Video Production",
                desc: "Cinematic drone footage for films, commercials, and promotional content.",
                icon: "🎥",
              },
              {
                title: "Inspections",
                desc: "Safe and efficient inspections of buildings, infrastructure, and industrial sites.",
                icon: "🏗️",
              },
              {
                title: "Surveying & Mapping",
                desc: "Accurate land surveys, topographic mapping, and 3D modeling services.",
                icon: "🗺️",
              },
              {
                title: "Agriculture",
                desc: "Crop monitoring, field analysis, and precision agriculture solutions.",
                icon: "🌱",
              },
              {
                title: "Emergency Services",
                desc: "Search and rescue, disaster assessment, and emergency response support.",
                icon: "⚡",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-100 rounded-xl p-6"
              >
                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-50 text-green-600 mb-4 text-lg">
                  {item.icon}
                </div>

                {/* Content */}
                <h3 className="font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Showcase */}
      <div className="container mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            How It Works
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="flex items-start p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex-shrink-0 mr-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-user-plus text-green-600"></i>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">For Providers</h3>
              <p className="text-gray-600">
                Register as Provider → Upload Credentials → Start Earning
              </p>
            </div>
          </div>

          <div className="flex items-start p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex-shrink-0 mr-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-search text-green-600"></i>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">For Seekers</h3>
              <p className="text-gray-600">
                Post Your Project → Compare Bids → Launch in 48 Hours
              </p>
            </div>
          </div>

          <div className="flex items-start p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex-shrink-0 mr-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-cogs text-green-600"></i>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Technology Integration</h3>
              <p className="text-gray-600">
                Seamless integration with Digital Sky Platform for flight
                approvals
              </p>
            </div>
          </div>

          <div className="flex items-start p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
            <div className="flex-shrink-0 mr-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-hand-holding-usd text-green-600"></i>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Secure Payments</h3>
              <p className="text-gray-600">
                Escrow-based payment protection for both providers and seekers
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Did you ever wonder */}
      <div className="bg-white py-24">
        <div className="container mx-auto px-6">
          {/* Heading */}
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-gray-900">
              Did You Ever Wonder?
            </h2>
            <p className="text-gray-500 mt-4 text-lg">
              Getting started is simple and straightforward
            </p>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 max-w-6xl mx-auto text-center">
            {/* Step 1 */}
            <div>
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl font-semibold mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Create Your Profile
              </h3>
              <p className="text-gray-600 max-w-sm mx-auto">
                Sign up as an operator or client and complete your profile with
                relevant details.
              </p>
            </div>

            {/* Step 2 */}
            <div>
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl font-semibold mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Connect & Collaborate
              </h3>
              <p className="text-gray-600 max-w-sm mx-auto">
                Browse projects or operators, communicate directly, and agree on
                terms.
              </p>
            </div>

            {/* Step 3 */}
            <div>
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-green-100 text-green-700 text-2xl font-semibold mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Get Results
              </h3>
              <p className="text-gray-600 max-w-sm mx-auto">
                Complete the project, receive deliverables, and process secure
                payments.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form section */}

      {/* <div ref={formSectionRef} className="bg-green-50 py-20">
<div className="bg-green-50 py-20">
  <div className="max-w-7xl mx-auto px-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">


      <div>

        <h2 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
          Ready to Earn <br /> Consistently?
        </h2>

        <p className="text-gray-600 max-w-md mb-8">
          Join our platform today and start connecting with opportunities.
          Whether you're an experienced operator or looking for drone services,
          we're here to help you succeed.
        </p>


        <div className="space-y-4 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-200 text-green-700 flex items-center justify-center">
              ✉️
            </div>
            <div>
              <p className="font-medium text-gray-900">Email Us</p>
              <p className="text-sm text-gray-600">support@droneplanet.in</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-200 text-green-700 flex items-center justify-center">
              📞
            </div>
            <div>
              <p className="font-medium text-gray-900">Call Us</p>
              <p className="text-sm text-gray-600">+91 97735 71992</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-200 text-green-700 flex items-center justify-center">
              ⏰
            </div>
            <div>
              <p className="font-medium text-gray-900">Business Hours</p>
              <p className="text-sm text-gray-600">
                Monday – Saturday: 9:30 AM – 5:30 PM
              </p>
            </div>
          </div>
        </div>


      </div>


      <div className="bg-white rounded-xl shadow-lg p-8 border">
  <h3 className="text-lg font-semibold text-gray-900 mb-2">
    Get in Touch
  </h3>
  <p className="text-sm text-gray-500 mb-6">
    Share your requirement and we’ll connect you with certified pilots.
  </p>

  <form onSubmit={handleSubmit} className="space-y-4">


    <div>
  <p className="text-sm font-medium text-gray-700 mb-2">
    I am a
  </p>
  <div className="flex gap-3">
    <button
      type="button"
      onClick={() => setUserType("Client")}
      className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition
        ${
          userType === "Client"
            ? "bg-green-600 text-white border-green-600"
            : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
        }`}
    >
      Client
    </button>

    <button
      type="button"
      onClick={() => setUserType("Operator")}
      className={`flex-1 py-2.5 rounded-lg border text-sm font-medium transition
        ${
          userType === "Operator"
            ? "bg-green-600 text-white border-green-600"
            : "bg-white text-gray-700 border-gray-300 hover:bg-green-50"
        }`}
    >
      Operator
    </button>
  </div>
</div>


    <input
      type="text"
      placeholder="Full Name"
      value={formData.name}
      onChange={(e) =>
        setFormData({ ...formData, name: e.target.value })
      }
      className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-600 outline-none"
      required
    />

    <input
      type="email"
      placeholder="Email Address"
      value={formData.email}
      onChange={(e) =>
        setFormData({ ...formData, email: e.target.value })
      }
      className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-600 outline-none"
      required
    />

    <input
      type="text"
      placeholder="Phone Number"
      value={formData.phone}
      onChange={(e) => {
        const value = e.target.value.replace(/\D/g, "");
        setFormData({ ...formData, phone: value });
      }}
      maxLength={10}
      className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-600 outline-none"
    />

    <Autocomplete
  apiKey={process.env.REACT_APP_GOOGLE_KEY}
  onPlaceSelected={(place) => {
    setFormData((prev) => ({
      ...prev,
      address: place.formatted_address || place.name || "",
    }));
  }}
  options={{
    types: ["(cities)"],
    componentRestrictions: { country: "in" },
  }}
  placeholder="Address"
  className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-600 outline-none"
/>



    <input
      type="text"
      placeholder="Service Name"
      value={formData.crop}
      onChange={(e) =>
        setFormData({ ...formData, crop: e.target.value })
      }
      className="w-full border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-green-600 outline-none"
    />

    <button
      type="submit"
      className="w-full bg-green-700 hover:bg-green-800 text-white font-medium py-3 rounded-lg transition"
    >
      Send Message
    </button>
  </form>
</div>


    </div>
  </div>
</div>
</div> */}

      {/* <div ref={daasRef}>
        <Daas onSuccess={() => setShowForm(false)} />
      </div> */}
      <div className="bg-green-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              Frequently Asked Questions
            </h2>
            <div className="w-16 h-1 bg-green-500 mx-auto" />
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            {/* Left Side Tabs */}
            <div className="lg:w-1/4 space-y-2">
              {Object.keys(categories).map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`w-full text-left px-4 py-2 rounded-lg font-medium transition ${
                    activeCategory === cat
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-green-50"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Right Side FAQ Items */}
            <div className="lg:w-3/4 space-y-4">
              {categories[activeCategory].map((item, idx) => (
                <FaqItem key={idx} {...item} />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Final CTA Section */}
      <div className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/90 to-green-900/70 z-10"></div>
        <img
          src="https://readdy.ai/api/search-image?query=stunning%20aerial%20drone%20view%20of%20city%20and%20landscape%2C%20birds%20eye%20perspective%20showing%20both%20urban%20and%20natural%20environments%2C%20dramatic%20lighting%2C%20cinematic%20quality%2C%20golden%20hour%2C%20high%20resolution%2C%20breathtaking%20vista%20from%20above&width=1440&height=400&seq=4&orientation=landscape"
          alt="Aerial view"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="relative z-20 container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">
            Ready to Transform Your Drone Operations?
          </h2>

          <div
            onClick={() => {
              setTimeout(() => {
                if (daasRef.current) {
                  const yOffset = -105;
                  const y =
                    daasRef.current.getBoundingClientRect().top +
                    window.pageYOffset +
                    yOffset;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
              }, 100);
            }}
            className="inline-flex items-center gap-2 text-white font-semibold text-lg cursor-pointer transition duration-300 group"
          >
            <span className="group-hover:underline group-hover:scale-105 transition-transform duration-300">
              Join Drone Planet Today
            </span>
            <IoArrowForwardOutline
              size={24}
              className="mt-[4px] group-hover:scale-110 transition-transform duration-300"
            />
          </div>
        </div>
      </div>
      {zohoPayload && <ZohoLeadForm data={zohoPayload} />}

      <Footer />
    </div>
  );
};

export default DaasLandingpage;
